import type { CCPreset, DeviceType } from '~/types/presets';
// FMSynth presets
import { getDefaultFMSynthPreset } from '~/config/presets/cc/fm/default-fm';
import { getWhaatFMSynthPreset } from '~/config/presets/cc/fm/whaat';
import { getInitFMSynthPreset } from '~/config/presets/cc/fm/init-fm';
import { getOGFMSynthPreset } from '~/config/presets/cc/fm/og-fm';
import { getRumblesFMSynthPreset } from '~/config/presets/cc/fm/rumbles-fm';
// MonoSynth presets
import { getDefaultMonosynthPreset } from '~/config/presets/cc/mono/default-mono';
import { getWellRoundedMonosynthPreset } from '~/config/presets/cc/mono/well-rounded';
import { getInitMonosynthPreset } from '~/config/presets/cc/mono/init-mono';
import { getOGMonosynthPreset } from '~/config/presets/cc/mono/og-mono';
import { getRumblesMonosynthPreset } from '~/config/presets/cc/mono/rumbles-mono';
// Hardware MIDI presets
import { getDefaultHardwarePreset } from '~/config/presets/cc/hardware/default-hardware';

/**
 * CCPresetManager
 *
 * Manages CC (Control Change) presets for different device types.
 * Provides methods to retrieve, create, and manage CC presets.
 *
 * Currently supports:
 * - FMSynth: FM synthesis parameters
 * - MonoSynth: Subtractive synthesis parameters
 * - MIDI: Generic hardware MIDI parameters
 *
 * Future functionality (when Supabase integration is added):
 * - Load user presets from database
 * - Save custom presets
 * - Share presets publicly
 * - Import/export presets
 */
export class CCPresetManager {
  private presets: Map<string, CCPreset> = new Map();

  constructor() {
    this.initializeSystemPresets();
  }

  /**
   * Initialize system presets (built-in defaults)
   */
  private initializeSystemPresets(): void {
    // FMSynth presets
    const defaultFM = getDefaultFMSynthPreset();
    const whaatFM = getWhaatFMSynthPreset();
    const initFM = getInitFMSynthPreset();
    const ogFM = getOGFMSynthPreset();
    const rumblesFM = getRumblesFMSynthPreset();

    // MonoSynth presets
    const defaultMono = getDefaultMonosynthPreset();
    const wellRoundedMono = getWellRoundedMonosynthPreset();
    const initMono = getInitMonosynthPreset();
    const ogMono = getOGMonosynthPreset();
    const rumblesMono = getRumblesMonosynthPreset();

    // Hardware MIDI presets
    const defaultHardware = getDefaultHardwarePreset();

    // Register all presets
    this.presets.set(defaultFM.id, defaultFM);
    this.presets.set(whaatFM.id, whaatFM);
    this.presets.set(initFM.id, initFM);
    this.presets.set(ogFM.id, ogFM);
    this.presets.set(rumblesFM.id, rumblesFM);

    this.presets.set(defaultMono.id, defaultMono);
    this.presets.set(wellRoundedMono.id, wellRoundedMono);
    this.presets.set(initMono.id, initMono);
    this.presets.set(ogMono.id, ogMono);
    this.presets.set(rumblesMono.id, rumblesMono);

    this.presets.set(defaultHardware.id, defaultHardware);
  }

  /**
   * Get a CC preset by ID
   * @param id - Preset ID
   * @returns CCPreset or undefined if not found
   */
  getPreset(id: string): CCPreset | undefined {
    return this.presets.get(id);
  }

  /**
   * Get the default CC preset for a device type
   * @param deviceType - Device type (FMSynth, MonoSynth, MIDI)
   * @returns CCPreset for the device type
   */
  getDefaultPresetForDeviceType(deviceType: DeviceType): CCPreset {
    switch (deviceType) {
      case 'FMSynth':
        return getDefaultFMSynthPreset();
      case 'MonoSynth':
        return getDefaultMonosynthPreset();
      case 'MIDI':
        return getDefaultHardwarePreset();
    }
  }

  /**
   * Get all CC presets for a specific device type
   * @param deviceType - Device type to filter by
   * @returns Array of CC presets
   */
  getPresetsForDeviceType(deviceType: DeviceType): CCPreset[] {
    return Array.from(this.presets.values()).filter(
      preset => preset.deviceType === deviceType
    );
  }

  /**
   * Get all available CC presets
   * @returns Array of all CC presets
   */
  getAllPresets(): CCPreset[] {
    return Array.from(this.presets.values());
  }

  /**
   * Add a new CC preset to the manager
   * @param preset - CC preset to add
   */
  addPreset(preset: CCPreset): void {
    this.presets.set(preset.id, preset);
  }

  /**
   * Remove a CC preset from the manager
   * @param id - Preset ID to remove
   * @returns true if removed, false if not found
   */
  removePreset(id: string): boolean {
    return this.presets.delete(id);
  }

  /**
   * Check if a preset exists
   * @param id - Preset ID
   * @returns true if preset exists
   */
  hasPreset(id: string): boolean {
    return this.presets.has(id);
  }

  /**
   * Get the number of presets
   * @returns Number of presets
   */
  getPresetCount(): number {
    return this.presets.size;
  }

  // Future methods (for Supabase integration):
  // - async loadUserPresets(userId: string): Promise<void>
  // - async savePreset(preset: CCPreset): Promise<void>
  // - async deletePreset(id: string): Promise<void>
  // - async sharePreset(id: string): Promise<void>
  // - async importPreset(json: string): Promise<CCPreset>
  // - exportPreset(id: string): string
}

// Singleton instance
let ccPresetManagerInstance: CCPresetManager | null = null;

/**
 * Get the CCPresetManager singleton instance
 */
export function getCCPresetManager(): CCPresetManager {
  if (!ccPresetManagerInstance) {
    ccPresetManagerInstance = new CCPresetManager();
  }
  return ccPresetManagerInstance;
}

/**
 * Reset the CCPresetManager singleton (useful for testing)
 */
export function resetCCPresetManager(): void {
  ccPresetManagerInstance = null;
}
